import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../../../utils/pricing';

// Navy chapter band (same grammar as home/page.tsx NavyBand). Band = divider (#31) / no repeated heading right after a band (#33).
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// ===== Second Steward detail (EN) — full modernization (Implementer A scope). English only, no Japanese chars. =====
// Design base: about/overview + home. Facts from working-tree EN/JA second-steward + JA hub pricing. No fabrication.

export default function EnSecondStewardPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Butler Services', href: '/en/butler-services/' },
    { label: 'Second Steward' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { en: 'Anticipation', t: 'Service Rooted in Deep Understanding', d: 'Through a continuous relationship, butlers anticipate unspoken needs. Preparations are complete before you ask—this is authentic butler service.' },
    { en: 'Quality of Life', t: 'Enhanced Quality of Life', d: 'Twenty days of monthly support dramatically improves your overall lifestyle. Gain time, reduce stress, and create room to focus on what truly matters.' },
    { en: 'Trust', t: 'Building Trust', d: 'Monthly service by the same butler deepens mutual trust. With family-like reassurance, entrust even private matters with confidence.' },
    { en: 'Flexible', t: 'Flexible Scheduling', d: 'Freely allocate 20 monthly days to match your schedule—weekdays only, four days weekly, or alternate days.' },
    { en: 'Value', t: 'Value of Continuity', d: `At ¥${yenInc(jbaDispatchExTax.secondStewardPerDay)} per day (¥${yenInc(jbaDispatchExTax.secondStewardMonthly)} ÷ 20 days), significantly more economical than single-use service (¥${yenInc(jbaDispatchExTax.butlerBase)}/day). Access high-quality service affordably.` },
  ];

  const scenes = [
    { t: 'Busy Executives', d: 'For executives who wish to entrust every daily task and focus on business—schedule management, guest reception, travel prep, and entertainment support across both business and home.' },
    { t: 'Care for Elderly Family', d: 'Meal preparation, medication management, appointment accompaniment, and companionship provided continuously. The butler notices daily changes and reports to the family as needed.' },
    { t: 'Prenatal & Childcare Support', d: 'During pregnancy and the demanding postpartum period, we provide general housework, care for older children, and nutrition-conscious meals with mother and child’s health first.' },
    { t: 'Villa & Private Office Management', d: 'We handle pre-arrival preparation, on-stay support, and post-departure management of villas. For home offices, we maintain the environment, receive guests, and assist with clerical work.' },
  ];

  const patterns = [
    { t: 'Weekdays Only', s: 'Mon–Fri, 5 days × 4 weeks = 20', d: 'The same hours daily, with consistent support from morning prep to dinner preparation.' },
    { t: 'Four Days a Week', s: 'Mon/Tue/Thu/Fri × 5 weeks = 20', d: 'Wednesday off, with intensive support on the other days.' },
    { t: 'Alternate Days', s: 'Every-other-day combination', d: 'Flexible use fully matched to your schedule.' },
    { t: 'Concentrated Use', s: '10 days each at month start & end', d: 'For especially busy periods such as before/after events or fiscal close.' },
  ];

  const serviceGroups = [
    { t: 'Complete Daily Life Support', d: 'General housework, meal preparation, shopping, laundry, cleaning, organizing, pet care, plant care.' },
    { t: 'Personal Assistant', d: 'Schedule assistance, reservation proxy, gift arrangement, travel preparation, seasonal wardrobe changes, seasonal event prep.' },
    { t: 'Business Support', d: 'Guest reception, meeting preparation, document sorting, mail management, office environment upkeep.' },
    { t: 'Healthcare Support', d: 'Appointment accompaniment, medication management, nutrition-conscious meals, observation and reporting of health status.' },
    { t: 'Events & Entertaining', d: 'Regular home-party preparation, entertainment support, planning and running seasonal events.' },
    { t: 'Residence Management', d: 'Regular cleaning, attending equipment inspections, arranging repairs, adjusting the living environment by season.' },
  ];

  const faqs = [
    ['What are the benefits of continuous use?', 'As the butler deeply understands you, optimal service can be provided without spoken instructions. The per-day rate is also far more economical than single use.'],
    ['Can I allocate the 20 days freely?', 'Yes, entirely. Twenty consecutive days, five days weekly, alternate days—set them to your schedule.'],
    ['Will the same butler come each time?', 'Yes, in principle the same butler serves you. When unavoidable substitutions occur, handovers are thorough.'],
    ['Can I change dates on short notice?', 'Yes, with notice by the day before. Rescheduling within the month is the standard.'],
    ['Can I try one month before continuing?', 'Yes, you can start with a one-month contract. If satisfied, it renews automatically. Comprehensive support for the whole family is also possible.'],
  ];

  const related = [
    { name: 'Footman', cad: 'Spot use from 3 hours', price: yenInc(jbaDispatchExTax.footmanBase), unit: '~ (incl. tax)', href: '/en/services/footman/' },
    { name: 'Butler', cad: '8 hours per day, dedicated', price: yenInc(jbaDispatchExTax.butlerBase), unit: '~ (incl. tax)', href: '/en/services/butler/' },
    { name: 'House Steward', cad: '24 hours, three-butler rotation', price: yenInc(jbaDispatchExTax.houseStewardMonthly), unit: ' (incl. tax)', href: '/en/services/house-steward/' },
  ];

  return (
    <Layout>
      <title>Second Steward | 20-Day Monthly Butler Service & Pricing | Japan Butler Association</title>
      <meta name="description" content={`THE BUTLER's Second Steward provides one dedicated butler 20 days a month, 8 hours daily. Truly personalized service through continuity, for affluent families and family offices. From ¥${yenInc(jbaDispatchExTax.secondStewardMonthly)} (incl. tax, monthly)—¥${yenInc(jbaDispatchExTax.secondStewardPerDay)} per day.`} />
      <link rel="canonical" href="https://butler-association.jp/en/services/second-steward" />

      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-fs-en-046-services_second-steward.webp" alt="Second Steward service. The same butler continuously supporting daily life" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Plan III — Second Steward</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.1vw,40px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">The same butler, 20 days,</span>
              <span className="block">ordering your daily life</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              Second Steward offers one dedicated butler for 20 days per month—an exquisite continuous plan. Through an ongoing relationship, your butler deeply understands your rhythms, preferences, and habits to provide truly personalized service. A supreme experience for affluent families, family offices, and VVIP clients.
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <NavyBand no="I" ja="About This Service" en="Overview" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S1 Overview */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              Continuity brings <span className="text-[#a38e65]">true personalization</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>Second Steward delivers the greatest value that one-time service can never provide: continuity. Over a month, the same butler visits regularly, deeply understanding your rhythms, preferences, and values to deliver attentive service as if serving you for years.</p>
              <p>The essence of butler service lies in knowing the client. From morning coffee strength to preferred newspapers, comfortable room temperature, and guest-reception protocols, the information a butler gathers daily is extensive. Through this process, we build a true partnership supporting your life—beyond mere service provision.</p>
              <p>Staffed exclusively by senior butlers certified by the Japan Butler Association, this service embodies the essence of more than 130 years of butler culture. Our professionals bring not just technique but dignity, refinement, and above all a servant’s heart.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 mt-10">
              {features.map((f) => (
                <div key={f.t} className="border-t border-[#e2ddd2] pt-6">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5">{f.t}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="II" ja="Usage Scenes" en="Scenes" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S2 Scenes */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Lifestyles where it delivers most
            </h2>
            <div className="grid lg:grid-cols-[340px_1fr] gap-8 lg:gap-12 items-start">
              <img src="/images/generated/jba-fs-242-services_second-steward.webp" alt="Second Steward providing daily support" className="w-full h-64 lg:h-[360px] object-cover border border-[#a38e65]/30" />
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {scenes.map((s) => (
                  <div key={s.t} className="border-l-2 border-[#a38e65]/40 pl-4">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="III" ja="Usage Patterns" en="How It Works" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S3 Usage Patterns */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              Ways to use 20 days
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              The 20 monthly days can be allocated freely. Below are representative patterns; we design the optimal schedule for your lifestyle.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {patterns.map((p) => (
                <div key={p.t} className="bg-[#f7f5f0] p-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{p.t}</h3>
                  <p className="font-['Playfair_Display',serif] text-sm text-[#a38e65] mb-3">{p.s}</p>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{p.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="IV" ja="What We Provide" en="What We Do" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S4 What We Do */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Comprehensive support through continuity
            </h2>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                {serviceGroups.map((g) => (
                  <div key={g.t} className="border-t border-[#e2ddd2] pt-6">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">{g.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{g.d}</p>
                  </div>
                ))}
              </div>
              <img src="/images/generated/jba-fs-243-services_second-steward.webp" alt="Second Steward supporting all aspects of daily life" className="w-full h-64 lg:h-[420px] object-cover border border-[#a38e65]/30 order-first lg:order-none" />
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="V" ja="Pricing" en="Fees" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S5 Pricing */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Clear and transparent pricing
            </h2>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start">
              <div className="bg-[#1a2942] p-8 text-center">
                <div className="font-['Noto_Serif_JP',serif] text-[38px] text-[#f8f6f0] leading-none">&yen;{yenInc(jbaDispatchExTax.secondStewardMonthly)}</div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] mt-3">incl. tax &middot; monthly</p>
                <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#d7b779] mt-4">&yen;{yenInc(jbaDispatchExTax.secondStewardPerDay)} per day<span className="text-xs text-[#ded8cc] ml-1">(20-day usage)</span></p>
              </div>
              <div>
                <dl className="border-t-2 border-[#1a2942]">
                  {[
                    ['Monthly Base Rate', `¥${yenInc(jbaDispatchExTax.secondStewardMonthly)}~ (incl. tax) / 8 hours × 20 days`],
                    ['Service Hours', '8 hours between 8:00 and 21:00'],
                    ['After Hours / Extension', `¥${yenInc(jbaDispatchExTax.secondStewardExtension)}/hour (incl. tax)`],
                    ['Additional Days', `¥${yenInc(jbaDispatchExTax.secondStewardAddDay)}/day (incl. tax)`],
                    ['Butler Selection', 'Free'],
                    ['Transportation', 'Monthly flat rate or actual expenses'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] shrink-0">{k}</dt>
                      <dd className="font-['Noto_Serif_JP',serif] text-[16px] text-[#1a2942] text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mt-6">
                  <span className="text-[#8f7a56]">Payment:</span> bank transfer (monthly) or credit card. 3% off for 3-month prepayment, 5% off for 6-month prepayment. Contracts renew monthly.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VI" ja="Frequently Asked Questions" en="FAQ" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S6 FAQ */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Frequently asked questions
            </h2>
            <div className="max-w-3xl divide-y divide-[#e2ddd2] border-t border-[#e2ddd2]">
              {faqs.map(([q, a]) => (
                <div key={q} className="py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">Q. {q}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VII" ja="Other Plans" en="More Options" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S7 Other Plans */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              Compare the plans
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              For a first trial choose Footman; for a special full day, Butler; to entrust the whole estate around the clock, House Steward.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <a key={r.name} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] transition-colors duration-300 p-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 group-hover:text-[#a38e65] transition-colors">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mb-4">{r.cad}</p>
                  <div className="font-['Noto_Serif_JP',serif] text-[22px] text-[#1a2942]">&yen;{r.price}<span className="text-sm text-[#8f7a56] ml-1">{r.unit}</span></div>
                </a>
              ))}
            </div>
            <div className="mt-8"><a href="/en/butler-services/" className={BTN08_ON_LIGHT}>Back to all services</a></div>
          </section>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S8 CTA */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <h2 className="jba-display font-medium text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.5] mb-4">Transform your life with Second Steward</h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                A dedicated butler will transform your lifestyle. Please feel free to consult us; we will listen carefully and propose the optimal plan and schedule.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                <span className="font-['Noto_Serif_JP',serif] text-[#e7e2d8] text-[15px]">Phone: 03-6824-4086 (Weekdays 10:00-18:00)</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
